/**
 * Account Mutation Hooks
 * Mutations for account management operations
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../useAuth";
import { apiDelete } from "../../../lib/api-utils";
import { useToast } from "../../use-toast";
import { ENDPOINTS, TOAST_MESSAGES, RETRY_CONFIG } from "../config";
import { queryKeys, getAccountRelatedQueryKeysToInvalidate } from "../query-keys";
import type { ResetResponse, RemoveConnectionResponse } from "../types";
import { getErrorMessage } from "../utils";

/**
 * Reset User Account
 * Clears all calendar connections and data for the user
 */
export const useResetAccountMutation = () => {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (): Promise<ResetResponse> => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const response = await apiDelete(ENDPOINTS.account.clear, token);

      return response as ResetResponse;
    },
    onSuccess: (data) => {
      toast({
        title: TOAST_MESSAGES.account.resetSuccess.title,
        description:
          data.message ||
          TOAST_MESSAGES.account.resetSuccess.description,
      });

      // Invalidate all queries
      queryClient.invalidateQueries();
    },
    onError: (error: any) => {
      const message = getErrorMessage(
        error,
        TOAST_MESSAGES.account.resetError.description
      );
      toast({
        title: TOAST_MESSAGES.account.resetError.title,
        description: message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Remove Connection
 * Disconnects a specific calendar provider (Google or Microsoft)
 */
export const useRemoveConnectionMutation = () => {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (
      connectionType: "google" | "microsoft" | "google-primary" | "google-secondary"
    ) => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      console.log("=== REACT QUERY MUTATION DEBUG ===");
      console.log("Connection type to remove:", connectionType);
      console.log("Auth token present:", !!token);

      const response = await apiDelete(
        `${ENDPOINTS.account.removeConnection}?connectionType=${encodeURIComponent(
          connectionType
        )}`,
        token
      );

      console.log("API response:", response);

      return response as RemoveConnectionResponse;
    },
    retry: RETRY_CONFIG.standard,
    onSuccess: async (data, connectionType) => {
      toast({
        title: TOAST_MESSAGES.account.connectionRemoved.title,
        description:
          data.message ||
          `${connectionType} connection removed successfully.`,
      });

      // Comprehensive query invalidation to ensure all related data is refreshed
      const keysToInvalidate = getAccountRelatedQueryKeysToInvalidate();
      keysToInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });

      // Force refetch of account status to update UI immediately
      await queryClient.refetchQueries({
        queryKey: queryKeys.account.status(),
      });
    },
    onError: (error: any, connectionType) => {
      const message = getErrorMessage(
        error,
        `Failed to remove ${connectionType} connection. Please try again.`
      );
      toast({
        title: TOAST_MESSAGES.account.connectionError.title,
        description: message,
        variant: "destructive",
      });
    },
  });
};
